import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, Check, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Loading } from "@/components/dash";
import { SubscribePanel } from "@/components/SubscribePanel";
import { RelatedReports } from "@/components/Related";

export function useCatalog(module) {
  return useQuery({ queryKey: ["catalog", module], queryFn: async () => (await api.get(`/catalog/${module}`)).data });
}

const Icon = ({ i }) => (i.owned ? <Check className="h-3 w-3 text-[#03AB9E]" /> : i.sample ? <Gift className="h-3 w-3 text-[#F58B23]" /> : <Lock className="h-3 w-3 text-slate-500" />);

export function ProductGate({ module, eyebrow, title, children }) {
  const [params, setParams] = useSearchParams();
  const { data } = useCatalog(module);
  if (!data) return <Loading />;
  const items = data.products;
  if (!items.length) return <Panel><p className="text-sm text-slate-400">No data available yet for this module.</p></Panel>;
  const product = params.get("product") || (items.find((i) => i.owned) || items.find((i) => i.sample) || items[0]).name;
  const cur = items.find((i) => i.name === product);
  const ownsAny = items.some((i) => i.owned);
  const locked = !cur?.entitled;

  const selector = (
    <Select value={product} onValueChange={(v) => setParams({ product: v })}>
      <SelectTrigger data-testid={`${module}-product-select`} className="w-[260px] h-9 bg-slate-900 border-[#1074BA]/60 text-sm"><SelectValue placeholder="Select product" /></SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 max-h-80">
        {items.map((i) => <SelectItem key={i.name} value={i.name}><span className="flex items-center gap-2"><Icon i={i} />{i.name}{i.sample && !i.owned && <span className="text-[10px] text-[#F58B23]">sample</span>}</span></SelectItem>)}
      </SelectContent>
    </Select>
  );
  const panel = <SubscribePanel key={locked ? product : "panel"} module={module} cat={data} collapsible={ownsAny && !locked} initial={locked ? [product] : []} />;

  if (locked)
    return (
      <div className="space-y-6">
        {panel}
        <PageHeader eyebrow={eyebrow} title={title}>{selector}</PageHeader>
        <Panel testId="locked-panel" className="text-center py-14">
          <Lock className="h-10 w-10 mx-auto text-[#F58B23] mb-4" />
          <h3 className="text-xl font-bold">{product} is not in your subscription</h3>
          <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">It's pre-selected in the subscription panel above. Choose regions and subscribe to unlock charts, tables and AI insights — or open the free sample <button className="text-[#F58B23] hover:underline" data-testid="locked-view-sample" onClick={() => setParams({ product: data.sample })}>{data.sample}</button>.</p>
        </Panel>
        <RelatedReports product={product} />
      </div>
    );
  return (
    <div className="space-y-6">
      {!ownsAny && panel}
      {cur.sample && !cur.owned && (
        <p className="flex items-center gap-2 text-xs text-[#F58B23] font-mono uppercase tracking-wider" data-testid="sample-banner"><Gift className="h-4 w-4" />Free sample · full data for {product}</p>
      )}
      {children({ product, selector, regions: cur.regions, allRegions: data.regions })}
      {ownsAny && panel}
      <RelatedReports product={product} />
    </div>
  );
}
